import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* Item 17 — measure "must not be split" ranges inside `node` (in CSS px,
 * relative to node's own top) so multi-page PDF exports can shift a page
 * break earlier rather than slicing through a table row or stranding a
 * section header from its first row. A header is merged with its table's
 * first row into one range so the header can never be left alone at the
 * bottom of a page. Returns ranges sorted ascending. */
function measureProtectedRanges(node) {
    const nodeTop = node.getBoundingClientRect().top;
    const ranges = [];
    node.querySelectorAll("[data-pdf-section]").forEach((section) => {
        const header = section.querySelector('[data-pdf-unit="header"]');
        const rows = section.querySelectorAll('[data-pdf-unit="row"]');
        if (rows.length === 0) return;
        const rowRanges = Array.from(rows).map((r) => {
            const rect = r.getBoundingClientRect();
            return [rect.top - nodeTop, rect.bottom - nodeTop];
        });
        if (header) {
            const hRect = header.getBoundingClientRect();
            rowRanges[0] = [hRect.top - nodeTop, rowRanges[0][1]];
        }
        ranges.push(...rowRanges);
    });
    return ranges.sort((a, b) => a[0] - b[0]);
}

/* Render a DOM node to a multi-page A4 PDF and trigger a download.
 * Works fully client-side (offline-friendly). The node should be rendered
 * with a white background at a fixed pixel width (~800px) for crisp output.
 *
 * Item 17 fix: html2canvas rasterizes the node into ONE flat image, so CSS
 * print break rules (break-inside-avoid etc.) have no effect on it — the
 * previous naive fixed-height slice could cut straight through a table row
 * or strand a section header at the bottom of a page. Page breaks are now
 * shifted earlier than the naive cut whenever that cut would land inside a
 * protected range (measured above), and each page is drawn from its own
 * cropped canvas slice (not the same full image redrawn at an offset) so the
 * shortened page doesn't bleed the next block's content into its blank space. */
export async function exportNodeToPDF(node, filename = "document.pdf") {
    if (!node) throw new Error("No node to export");
    const protectedRangesCss = measureProtectedRanges(node);
    const nodeHeightCss = node.scrollHeight || 1;

    const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
    });

    const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 24;
    const usableW = pageW - margin * 2;
    const usableH = pageH - margin * 2;

    const imgW = usableW;
    const imgH = (canvas.height * imgW) / canvas.width;

    // pt <-> canvas-px conversion (uniform, since width scaling is uniform).
    const pxPerPt = canvas.height / imgH;
    // node CSS-px <-> canvas-px conversion.
    const pxPerCssPx = canvas.height / nodeHeightCss;
    const protectedRangesPx = protectedRangesCss.map(([s, e]) => [s * pxPerCssPx, e * pxPerCssPx]);
    const usableHpx = usableH * pxPerPt;

    // Compute page-break positions in canvas-px, shifting a naive cut earlier
    // whenever it would land inside a protected range.
    const pageRangesPx = [];
    let top = 0;
    while (top < canvas.height - 0.5) {
        let bottom = Math.min(top + usableHpx, canvas.height);
        if (bottom < canvas.height) {
            const hit = protectedRangesPx.find(([s, e]) => s < bottom && bottom < e);
            // Only shift if it makes forward progress — a single block taller
            // than one full page (extremely rare) falls back to the naive cut.
            if (hit && hit[0] > top) bottom = hit[0];
        }
        pageRangesPx.push([top, bottom]);
        top = bottom;
    }

    pageRangesPx.forEach(([topPx, bottomPx], i) => {
        const sliceHeightPx = Math.max(1, bottomPx - topPx);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, topPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
        const img = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceHeightPt = sliceHeightPx / pxPerPt;
        if (i > 0) pdf.addPage();
        pdf.addImage(img, "JPEG", margin, margin, imgW, sliceHeightPt, undefined, "FAST");
    });

    pdf.save(filename);
}
